// lib/card-presenters/note.js — NOTE CARD PRESENTERS
// ═══════════════════════════════════════════════════════════
// Each note type gets its own presenter. Pure functions — take a
// note row + a context (handlers, selectMode, isSelected), return
// ShiftCard props. Keeps per-type data decisions out of the feature
// file so we can iterate Note, Voice, Idea, Highlight independently.
//
// All four types use `hidePreview: true` so the card's "Core insight"
// block doesn't render — these are light-capture items, not analyzed
// artifacts. Context lives in `summary_bullets` (the muted em-dash
// lines at the bottom of the card body) and the footer.
// ═══════════════════════════════════════════════════════════

import { NOTE_TYPE_COLORS, NOTE_TYPE_LABELS } from '../../hooks/use-notes';

// ── Dispatcher ────────────────────────────────────────────
export function toNoteCard(note, ctx) {
  switch (note.note_type) {
    case 'voice':     return presentVoice(note, ctx);
    case 'idea':      return presentIdea(note, ctx);
    case 'highlight': return presentHighlight(note, ctx);
    case 'note':
    default:          return presentNote(note, ctx);
  }
}

// ── Note (typed manually) ─────────────────────────────────
// Title prefers user-set title. When Haiku has generated a quality
// description + bullets, those win over the mechanical sentence
// slicer — same visual, higher-quality signal.
function presentNote(note, ctx) {
  const userTitle = (note.title || '').trim();
  if (hasAiSummary(note)) {
    const title = userTitle || truncate(note.description, 60);
    return baseProps(note, ctx, { title, bullets: note.summary_bullets.slice(0, 2) });
  }
  const sentences = splitSentences(note.content || '');
  const title = userTitle || truncate(sentences[0] || 'Untitled note', 60);
  const remainingSentences = userTitle ? sentences : sentences.slice(1);
  const bullets = pickBullets(remainingSentences, 2, 120);
  return baseProps(note, ctx, { title, bullets });
}

// ── Voice (transcribed from "Hey {ai_name}" wake word) ────
// Voice notes are where AI summary matters most — transcripts start
// with filler and meander. When Haiku summary has landed we show it
// and keep a trailing "≈ N words" marker for length context.
function presentVoice(note, ctx) {
  const content = note.content || '';
  const wordCount = countWords(content);
  if (hasAiSummary(note)) {
    const title = truncate(note.description || 'Voice note', 80);
    const bullets = [...note.summary_bullets.slice(0, 1)];
    if (wordCount >= 30) bullets.push(`≈ ${wordCount} words`);
    return baseProps(note, ctx, { title, bullets });
  }
  const sentences = splitSentences(content);
  const title = truncate(sentences[0] || 'Voice note', 80);
  const bullets = [];
  if (sentences[1]) bullets.push(truncate(sentences[1], 120));
  if (wordCount >= 30) bullets.push(`≈ ${wordCount} words`);
  return baseProps(note, ctx, { title, bullets });
}

// ── Idea (auto-extracted [IDEA:...] from AI messages) ─────
// Ideas are typically one sentence, so the full text fits as the
// title. Bullet just marks it as captured from a conversation so the
// user has context without clicking through.
function presentIdea(note, ctx) {
  const text = (note.content || '').trim();
  const title = truncate(text || 'Idea', 120);
  const bullets = note.session_id ? ['Captured in conversation'] : [];
  return baseProps(note, ctx, { title, bullets });
}

// ── Highlight (text selection from a chat message) ────────
// The source snippet is the thing worth showing. Title is a stable
// header with a date stamp; the bullet holds the quoted snippet so
// it reads the way the user captured it. If they added their own
// reflection later (content diverged from source_text), a second
// bullet carries that note.
function presentHighlight(note, ctx) {
  const snippet = (note.source_text || note.content || '').trim();
  const reflection = (note.content || '').trim();
  const hasReflection = reflection && reflection !== snippet && reflection.length > 0;
  const dateLabel = note.created_at
    ? new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const title = dateLabel ? `Highlight · ${dateLabel}` : 'Highlight';
  const bullets = [];
  if (snippet) bullets.push(`"${truncate(snippet, 180)}"`);
  if (hasReflection) bullets.push(truncate(reflection, 160));
  return baseProps(note, ctx, { title, bullets });
}

// ── Shared shape ──────────────────────────────────────────
function baseProps(note, ctx, { title, bullets }) {
  return {
    key: note.id,
    item: {
      id: note.id,
      title,
      created_at: note.created_at,
      summary_bullets: bullets,
      session_title: null, // TODO: resolve session_id → title in a future polish pass
    },
    accentColor: NOTE_TYPE_COLORS[note.note_type] || '#888',
    typeLabel: NOTE_TYPE_LABELS[note.note_type] || 'note',
    hidePreview: true,
    onOpen: () => ctx.onOpen?.(note),
    openLabel: ctx.openLabelFor?.(note) || 'Open →',
    onDelete: () => ctx.onDelete?.(note),
    selectMode: ctx.selectMode,
    isSelected: ctx.isSelected?.(note.id),
    onSelect: () => ctx.onSelect?.(note),
  };
}

// ── Helpers ───────────────────────────────────────────────

function hasAiSummary(note) {
  return Boolean(note?.description) || (Array.isArray(note?.summary_bullets) && note.summary_bullets.length > 0);
}

function splitSentences(s) {
  if (!s) return [];
  return s
    .split(/(?<=[.!?])\s+|\n+/)
    .map(x => x.trim())
    .filter(Boolean);
}

function pickBullets(sentences, max, maxChars) {
  const out = [];
  for (const s of sentences) {
    if (out.length >= max) break;
    const t = truncate(s, maxChars);
    if (t) out.push(t);
  }
  return out;
}

function truncate(str, n) {
  if (!str) return '';
  if (str.length <= n) return str;
  return str.slice(0, n - 1).trimEnd() + '…';
}

function countWords(s) {
  if (!s) return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
}
